/**
 * EDEN — Table of Specifications (TOS) engine.
 *
 * Pure module: no network, no database, no DOM. Everything here is a function of its arguments.
 * This is the one part of the assessment module that must never be arithmetically wrong, so it is
 * kept isolated and unit-tested against the department's real spreadsheet
 * (`Syllabus and tests Samples/EN10 TOS FOR JUNE 2025-2026.xlsx`).
 *
 * The arithmetic is the department's own, transcribed from that workbook's formulas:
 *
 *   mark(item)        = paperMarks × time(item) / paperMinutes          (J5: =$T$5*K5/$S$5)
 *   p(item)           = correct × 100 / attempted   -- a PERCENTAGE     (Q5: =R5*100/$O$5)
 *   percentage(sec)   = time(sec) × 100 / Σ time(sec)                   (L27: =C27/$C$31*100)
 *   mark(sec)         = percentage(sec) × paperMarks / 100              (D27: =L27*30/100)
 *   K/C/A(sec)        = mark(sec) × 0.5 / 0.3 / 0.2                     (E27/F27/G27)
 *   easy/med/diff(sec)= items(sec) × 0.2 / 0.6 / 0.2                    (H27/I27/J27)
 *   questions(sec)    = percentage(sec) × Σ items / Σ percentage        (K27: =L27*$M$27/$L$31)
 *
 * Guideline constants come from `Syllabus and tests Samples/guidelines for SA.jpg`.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Bloom level as the department writes it in the TOS: k(nowledge), c(omprehension), a(pplication). */
export type Bloom = "k" | "c" | "a";

/** Difficulty label. Three bands, matching the MoEYS training deck. */
export type DifficultyBand = "easy" | "medium" | "difficult";

/** What the MoEYS quality-control table says to do with an item after it has been sat. */
export type ItemVerdict = "drop_too_hard" | "keep" | "drop_too_easy" | "unknown";

export type AssessmentKind = "monthly" | "sa1" | "sa2" | "mock";

export interface TosItem {
  id: string;
  /** Printed item number within the paper. */
  itemNo: number;
  /** Printed question code, e.g. "II.B". Empty string counts as missing. */
  questionCode?: string;
  /** The ONLY mark input. Marks are derived from time, never typed in. */
  timeMinutes: number;
  bloom: Bloom;
  /** Controlled vocabulary; see LISTENING_FORMS for the subset that requires media. */
  questionForm: string;
  /** True when the answer key says "Personal" — an open response with no fixed answer. */
  isOpenResponse?: boolean;
  /** Present when the item needs a recording, a dictation text or a picture. */
  media?: { audio?: string; image?: string; dictation?: string } | null;
  /** Filled in after the paper has been sat; drives difficulty and the keep/drop verdict. */
  stat?: { attempted: number; correct: number } | null;
}

export interface TosSection {
  id: string;
  /** Roman numeral as printed, e.g. "III". */
  roman: string;
  /** Controlled title, e.g. "Reading Comprehension". */
  title: string;
  /** The mark printed in the section heading — what the paper claims. */
  printedMarks: number;
  items: TosItem[];
}

export interface TosAssessment {
  id: string;
  kind: AssessmentKind;
  /** 1 = ORAL, 2 = WRITTEN. The department's convention, and it is counter-intuitive. */
  paper: 1 | 2;
  grade: number;
  /** The mark printed in the header, e.g. 30 or 70. */
  totalMarks: number;
  /** Printed duration. Null for oral papers, which print no duration. */
  durationMin: number | null;
  sections: TosSection[];
}

export interface Violation {
  code: string;
  severity: "error" | "warning";
  message: string;
  /** Section id, item id, or undefined for paper-level findings. */
  ref?: string;
}

export interface ItemMark {
  itemId: string;
  /** Unrounded, exactly as the spreadsheet computes it. */
  mark: number;
  /** What would be printed on the paper at the given precision. */
  printedMark: number;
  /** printedMark - mark. Non-zero means the paper does not add up to the blueprint. */
  residual: number;
}

export interface SectionQuota {
  sectionId: string;
  timeMinutes: number;
  /** Share of total time, 0–100. */
  percentage: number;
  /** Mark this section should carry, derived from its share of time. */
  mark: number;
  bloomMarks: { k: number; c: number; a: number };
  /** Target item counts by difficulty, from the 20/60/20 guideline. */
  difficultyCounts: { easy: number; medium: number; difficult: number };
  /** Suggested number of questions, from the section's share of total percentage. */
  questionCount: number;
}

// ---------------------------------------------------------------------------
// Guideline constants — `guidelines for SA.jpg`
// ---------------------------------------------------------------------------

export const BLOOM_SPLIT = { k: 0.5, c: 0.3, a: 0.2 } as const;
export const DIFFICULTY_SPLIT = { easy: 0.2, medium: 0.6, difficult: 0.2 } as const;
export const ITEM_COUNT_RANGE = { min: 15, max: 30 } as const;
export const TOP_MARK_RANGE = { min: 50, max: 100 } as const;
export const ENGLISH_DURATION_MIN = 90;

/** How far a paper may drift from the Bloom / difficulty splits before it is flagged. */
export const SPLIT_TOLERANCE = 0.05;

/** Marks are printed to this many decimals on the real papers — i.e. whole marks. */
export const PRINTED_DECIMALS = 0;

/** Question forms whose stimulus lives outside the page and therefore requires media. */
export const LISTENING_FORMS = new Set([
  "listening_mcq",
  "listening_open",
  "dictation",
  "spelling",
  "sound_and_read",
]);

/**
 * Which difficulty scale is authoritative.
 *
 * "moeys"       — the training deck the department cites: easy 75–100, medium 25–75, difficult 0–25,
 *                 and the quality rule p<20 or p>80 means retire the item.
 * "spreadsheet" — the live IF() formula in the school's workbook (81/76/26/20). Kept so the old
 *                 numbers can be reproduced, but it disagrees with the source it cites.
 *
 * Default is "moeys". Changing this is a one-line change and affects only labelling, never marks.
 */
export type DifficultyScale = "moeys" | "spreadsheet";
export const DEFAULT_DIFFICULTY_SCALE: DifficultyScale = "moeys";

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const round = (n: number, dp: number): number => {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
};

/** Float-tolerant comparison; marks are computed from divisions and never land exactly. */
const near = (a: number, b: number, eps = 1e-6): boolean => Math.abs(a - b) <= eps;

const sum = (ns: number[]): number => ns.reduce((a, b) => a + b, 0);

export const allItems = (a: TosAssessment): TosItem[] => a.sections.flatMap((s) => s.items);

// ---------------------------------------------------------------------------
// p-value, difficulty, verdict
// ---------------------------------------------------------------------------

/**
 * Facility index as a PERCENTAGE, 0–100 — matching the workbook's `=R5*100/$O$5`.
 * Returns null when the item has not been sat, so callers must handle "not known yet"
 * rather than seeing a misleading 0.
 */
export function pValue(stat: { attempted: number; correct: number } | null | undefined): number | null {
  if (!stat || stat.attempted <= 0) return null;
  return (stat.correct * 100) / stat.attempted;
}

/** Difficulty label from a percentage p. Boundaries are inclusive at the lower edge. */
export function difficultyBand(
  p: number | null,
  scale: DifficultyScale = DEFAULT_DIFFICULTY_SCALE,
): DifficultyBand | null {
  if (p === null) return null;
  if (scale === "spreadsheet") {
    if (p >= 76) return "easy";
    if (p >= 26) return "medium";
    return "difficult";
  }
  if (p >= 75) return "easy";
  if (p >= 25) return "medium";
  return "difficult";
}

/**
 * The MoEYS quality-control rule the department's own spreadsheet documents but cannot execute:
 * below 20% the item is too hard to keep; above 80% it is too easy. Everything between is a
 * good question and goes back in the bank.
 */
export function itemVerdict(p: number | null): ItemVerdict {
  if (p === null) return "unknown";
  if (p < 20) return "drop_too_hard";
  if (p > 80) return "drop_too_easy";
  return "keep";
}

// ---------------------------------------------------------------------------
// Marks
// ---------------------------------------------------------------------------

/**
 * Per-item marks, derived from time.
 *
 * `paperMinutes` is the paper's printed duration (the workbook's $S$5 = 90 for a 90-minute English
 * paper), NOT the sum of item times. The two differ in the real workbook and that is a finding,
 * not something to silently reconcile — `validate` reports it.
 */
export function computeMarks(items: TosItem[], paperMarks: number, paperMinutes: number): ItemMark[] {
  if (paperMinutes <= 0) throw new RangeError("paperMinutes must be > 0");
  return items.map((it) => {
    const mark = (paperMarks * it.timeMinutes) / paperMinutes;
    const printedMark = round(mark, PRINTED_DECIMALS);
    return { itemId: it.id, mark, printedMark, residual: printedMark - mark };
  });
}

/**
 * Section-level quotas: the summary grid at the bottom of the workbook.
 *
 * Note the workbook derives these from the SUM OF SECTION TIMES (its C31 = 28), not from the
 * paper's printed duration. That is reproduced faithfully here.
 */
export function computeSectionQuotas(sections: TosSection[], paperMarks: number): SectionQuota[] {
  const times = sections.map((s) => sum(s.items.map((i) => i.timeMinutes)));
  const totalTime = sum(times);
  if (totalTime <= 0) throw new RangeError("total section time must be > 0");
  const totalItems = sum(sections.map((s) => s.items.length));

  // Σ percentage is 100 by construction; kept explicit because the workbook divides by its own L31.
  const percentages = times.map((t) => (t * 100) / totalTime);
  const totalPercentage = sum(percentages);

  return sections.map((s, idx) => {
    const timeMinutes = times[idx] ?? 0;
    const percentage = percentages[idx] ?? 0;
    const mark = (percentage * paperMarks) / 100;
    const n = s.items.length;
    return {
      sectionId: s.id,
      timeMinutes,
      percentage,
      mark,
      bloomMarks: {
        k: mark * BLOOM_SPLIT.k,
        c: mark * BLOOM_SPLIT.c,
        a: mark * BLOOM_SPLIT.a,
      },
      difficultyCounts: {
        easy: n * DIFFICULTY_SPLIT.easy,
        medium: n * DIFFICULTY_SPLIT.medium,
        difficult: n * DIFFICULTY_SPLIT.difficult,
      },
      questionCount: totalPercentage > 0 ? (percentage * totalItems) / totalPercentage : 0,
    };
  });
}

/** Actual Bloom split by MARK, as fractions of the paper. Marks, not item counts — the guideline is a mark split. */
export function bloomSplit(a: TosAssessment): { k: number; c: number; a: number } {
  const marks = computeMarks(allItems(a), a.totalMarks, a.durationMin ?? sum(allItems(a).map((i) => i.timeMinutes)));
  const byId = new Map(marks.map((m) => [m.itemId, m.mark]));
  const total = sum(marks.map((m) => m.mark));
  if (total <= 0) return { k: 0, c: 0, a: 0 };
  const acc = { k: 0, c: 0, a: 0 };
  for (const it of allItems(a)) acc[it.bloom] += byId.get(it.id) ?? 0;
  return { k: acc.k / total, c: acc.c / total, a: acc.a / total };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Every rule the department already writes down, plus the arithmetic ones a spreadsheet cannot
 * enforce on itself. Returns findings; never mutates the assessment.
 *
 * Errors block an assessment moving past `tos_ok`. Warnings are shown and can be accepted.
 */
export function validate(
  a: TosAssessment,
  opts: { scale?: DifficultyScale } = {},
): Violation[] {
  const v: Violation[] = [];
  const scale = opts.scale ?? DEFAULT_DIFFICULTY_SCALE;
  const items = allItems(a);
  const isSA = a.kind === "sa1" || a.kind === "sa2";

  // --- 1. Section marks must sum to the printed header total. -------------
  // This is the rule that catches EN10 SA1 Paper 2: header /70, sections total 71.
  const printedSum = sum(a.sections.map((s) => s.printedMarks));
  if (!near(printedSum, a.totalMarks, 1e-9)) {
    v.push({
      code: "sections-sum",
      severity: "error",
      message: `Section marks total ${printedSum} but the header says /${a.totalMarks}.`,
    });
  }

  if (items.length === 0) {
    v.push({ code: "no-items", severity: "error", message: "The paper has no items." });
    return v;
  }

  // --- 2. Time bookkeeping. -----------------------------------------------
  const itemTime = sum(items.map((i) => i.timeMinutes));
  if (a.durationMin !== null && !near(itemTime, a.durationMin, 1e-9)) {
    v.push({
      code: "time-mismatch",
      severity: "warning",
      message:
        `Item times total ${round(itemTime, 2)} min but the paper is printed as ${a.durationMin} min. ` +
        `Item marks are derived from the printed duration; section quotas are derived from the item times.`,
    });
  }

  // --- 3. Rounding residuals: what the paper prints vs what the blueprint says. ---
  const basis = a.durationMin ?? itemTime;
  const marks = computeMarks(items, a.totalMarks, basis);
  const markById = new Map(marks.map((m) => [m.itemId, m]));
  for (const s of a.sections) {
    const computed = sum(s.items.map((i) => markById.get(i.id)?.mark ?? 0));
    const printedItems = sum(s.items.map((i) => markById.get(i.id)?.printedMark ?? 0));
    if (!near(computed, s.printedMarks, 0.005)) {
      v.push({
        code: "section-rounding",
        severity: "warning",
        ref: s.id,
        message:
          `${s.roman}. ${s.title} prints ${s.printedMarks} but the blueprint computes ` +
          `${round(computed, 2)} (items round to ${round(printedItems, 2)}). ` +
          `Residual ${round(s.printedMarks - computed, 2)} — decide it, do not absorb it.`,
      });
    }
  }

  // --- 4. Guideline ranges. SA papers only; monthly tests are /30 by design. ---
  if (isSA) {
    if (items.length < ITEM_COUNT_RANGE.min || items.length > ITEM_COUNT_RANGE.max) {
      v.push({
        code: "item-count",
        severity: "error",
        message: `${items.length} items; the guideline is ${ITEM_COUNT_RANGE.min}–${ITEM_COUNT_RANGE.max}.`,
      });
    }
    if (a.totalMarks < TOP_MARK_RANGE.min || a.totalMarks > TOP_MARK_RANGE.max) {
      v.push({
        code: "top-mark",
        severity: "error",
        message: `Top mark ${a.totalMarks}; the guideline is ${TOP_MARK_RANGE.min}–${TOP_MARK_RANGE.max}.`,
      });
    }
  }

  // --- 5. Duration. Written English papers are 90 minutes at every grade. ---
  if (a.paper === 2 && a.durationMin !== null && a.durationMin !== ENGLISH_DURATION_MIN) {
    v.push({
      code: "duration",
      severity: "warning",
      message: `Written English papers are ${ENGLISH_DURATION_MIN} minutes; this one says ${a.durationMin}.`,
    });
  }

  // --- 6. Bloom split, by mark. -------------------------------------------
  const bs = bloomSplit(a);
  for (const level of ["k", "c", "a"] as const) {
    const target = BLOOM_SPLIT[level];
    if (Math.abs(bs[level] - target) > SPLIT_TOLERANCE) {
      v.push({
        code: `bloom-split-${level}`,
        severity: "warning",
        message:
          `${level.toUpperCase()} is ${(bs[level] * 100).toFixed(1)}% of marks; ` +
          `the guideline is ${(target * 100).toFixed(0)}% (±${(SPLIT_TOLERANCE * 100).toFixed(0)} pts).`,
      });
    }
  }

  // --- 7. Difficulty split — only meaningful once the paper has been sat. ---
  const banded = items
    .map((i) => difficultyBand(pValue(i.stat), scale))
    .filter((b): b is DifficultyBand => b !== null);
  if (banded.length === items.length) {
    for (const band of ["easy", "medium", "difficult"] as const) {
      const share = banded.filter((b) => b === band).length / banded.length;
      if (Math.abs(share - DIFFICULTY_SPLIT[band]) > SPLIT_TOLERANCE) {
        v.push({
          code: `difficulty-split-${band}`,
          severity: "warning",
          message:
            `${band} items are ${(share * 100).toFixed(1)}%; ` +
            `the guideline is ${(DIFFICULTY_SPLIT[band] * 100).toFixed(0)}%.`,
        });
      }
    }
  }

  // --- 8. Question codes: present, and sequential within their section. ----
  for (const s of a.sections) {
    for (const it of s.items) {
      if (!it.questionCode || it.questionCode.trim() === "") {
        v.push({
          code: "missing-question-code",
          severity: "error",
          ref: it.id,
          message: `Item ${it.itemNo} has no question code.`,
        });
      }
    }
  }
  const nos = items.map((i) => i.itemNo);
  for (let i = 1; i < nos.length; i++) {
    const prev = nos[i - 1] ?? 0;
    const cur = nos[i] ?? 0;
    if (cur <= prev) {
      v.push({
        code: "item-numbering",
        severity: "error",
        message: `Item numbers are not ascending: ${prev} then ${cur}.`,
      });
      break;
    }
  }

  // --- 9. Stimulus that lives off the page must be attached. --------------
  for (const it of items) {
    if (!LISTENING_FORMS.has(it.questionForm)) continue;
    const m = it.media;
    const hasStimulus = !!(m && (m.audio || m.dictation));
    if (!hasStimulus) {
      v.push({
        code: "missing-media",
        severity: "error",
        ref: it.id,
        message:
          `Item ${it.itemNo} is a "${it.questionForm}" item with no attached audio or dictation text. ` +
          `The stimulus would exist nowhere and the invigilator script would be blank.`,
      });
    }
  }

  // --- 10. Open responses need a marking note, not just "Personal". -------
  for (const it of items) {
    if (it.isOpenResponse && (markById.get(it.id)?.mark ?? 0) > 1) {
      v.push({
        code: "open-response-unrubriced",
        severity: "warning",
        ref: it.id,
        message: `Item ${it.itemNo} is an open response worth more than 1 mark; it needs criteria, not "Personal".`,
      });
    }
  }

  return v;
}

/** Convenience: does this assessment pass the gate to `tos_ok`? */
export function passesTosGate(a: TosAssessment, opts: { scale?: DifficultyScale } = {}): boolean {
  return validate(a, opts).every((x) => x.severity !== "error");
}
